<script setup lang="ts">
import { useAcl } from "vue-simple-acl";
import { SearchMode } from "~/core/composables/useSearch";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import LxcError from "~/core/models/LxcError.class";
import { PATH_BUSINESS_UNITS_ID_SETTINGS_DESCRIPTION } from "~/modules/businessUnit/constants";
import businessUnitService from "~/modules/businessUnit/services/businessUnit.service";
import { useOrganization } from "~/modules/organization/composables/useOrganization";
import type { OrganizationV2 } from "~/modules/organization/models/OrganizationV2.interface";
import { PATH_SUPER_ADMINISTRATION } from "~/modules/superAdministration/constants";
import { UserSessionEntityType } from "~/modules/user/models/UserSessionEntityType.enum";
import { useUserSession } from "~/modules/user/stores/useUserSession";
import ILxcChevronRight from "~icons/lxc/chevron-right";
import ILxcPlus from "~icons/lxc/plus";
import ILxcSettings from "~icons/lxc/settings";

const route = useRoute();
const { t } = useI18n();
const router = useRouter();

const {
  isLoading,
  results,
  error,
  fetchData,
  filters,
  setFilter,
  search,
  onSearch,
} = useOrganization(SearchMode.FILTER_SEARCH);
const { userSession } = useUserSession();
const { can } = useAcl();

const businessUnitUid: Ref<number> = ref(parseInt(route.params.id.toString()));
const businessUnitName: Ref<string | undefined> = ref("");
const superAdministrationName: Ref<string | undefined> = ref("");
const organizationList: Ref<OrganizationV2[]> = ref([]);
const showCreateOrganizationForm: Ref<boolean> = ref(false);
const isLoadingName: Ref<boolean> = ref(false);

const canAccessSuperAdmin: ComputedRef<boolean> = computed(() => {
  return (
    userSession?.entity.type === UserSessionEntityType.SUPER_ADMINISTRATION
  );
});

async function loadData() {
  error.value = undefined;
  await fetchData(businessUnitUid.value);
  if (!error.value) {
    organizationList.value = results.value as OrganizationV2[];
  }
}

async function getBusinessUnitInfo() {
  isLoadingName.value = true;
  const response = await businessUnitService.getBusinessUnit(
    businessUnitUid.value,
  );
  if (LxcError.check(response)) {
    error.value = response;
  } else {
    businessUnitName.value = response?.name;
    superAdministrationName.value = response?.superAdministrationName;
  }
  isLoadingName.value = false;
}

function goToSettings() {
  router.push({
    name: PATH_BUSINESS_UNITS_ID_SETTINGS_DESCRIPTION,
    params: { id: businessUnitUid.value },
  });
}

function onAddOrganization() {
  showCreateOrganizationForm.value = true;
}

getBusinessUnitInfo();

onSearch(loadData);
</script>

<template>
  <page-component :name="''" :should-not-translate="true">
    <template #breadcrumb>
      <div v-if="!isLoadingName" class="flex items-center">
        <router-link
          v-if="canAccessSuperAdmin"
          :to="PATH_SUPER_ADMINISTRATION"
          class="!text-gray-900 !no-underline hover:opacity-65"
          >{{ superAdministrationName }}</router-link
        >
        <template v-else>{{ superAdministrationName }}</template>
        <i-lxc-chevron-right class="h-6 w-6 mx-4 my-1" />
        {{ businessUnitName }}
      </div>
    </template>
    <template #subtitle>
      <lxc-button
        v-if="!isLoadingName"
        html-type="button"
        type="borderless"
        :icon="ILxcSettings"
        class="!px-0 !py-0 ml-4"
        icon-class="!w-6 h-6"
        :title="t('administration.businessUnit.settings.button')"
        @click="goToSettings"
      />
    </template>
    <template v-if="can(ACLRoles.CAN_WRITE_ORG)" #top-right>
      <lxc-button
        html-type="button"
        type="primary"
        :icon="ILxcPlus"
        :text="t('administration.businessUnit.addOrganization.button')"
        :title="t('administration.businessUnit.addOrganization.button')"
        @click="onAddOrganization"
      />
    </template>
    <organization-card-list
      :organizations="organizationList"
      :search="search"
      :on-search="onSearch"
      :filters="filters"
      :set-filter="setFilter"
      :is-loading="isLoading"
      :error="error"
    />
    <organization-create
      v-model:show="showCreateOrganizationForm"
      :business-unit-code="businessUnitUid"
      @create:success="loadData"
    />
  </page-component>
</template>
